import _extends from '@babel/runtime/helpers/esm/extends';
import { css as css$1, jsx as jsx$1, Global, ThemeContext } from '@emotion/core';
export { CacheProvider, ClassNames, Global, ThemeContext, withEmotionCache } from '@emotion/core';
import { transform, createBox, createColorModeProvider, createColorStyles, useThemeBreakpoints, useThemeBreakpoint, useThemeUp, useThemeDown } from '@xstyled/core';
export { getColorModeInitScriptElement, getColorModeInitScriptTag, useColorMode, useViewportWidth } from '@xstyled/core';
import { flatten, cascade } from '@xstyled/util';
import React from 'react';
import { withTheme, ThemeProvider, useTheme } from 'emotion-theming';
export { ThemeProvider, useTheme, withTheme } from 'emotion-theming';
import emStyled from '@emotion/styled';
export * from '@xstyled/system';

function styleToString(style, props) {
  if (Array.isArray(style)) return style.reduce(function (str, style) {
    return str + styleToString(style, props);
  }, '');
  if (typeof style === 'function') return styleToString(style(props), props);
  return style;
}

function css(strings) {
  for (var _len = arguments.length, rawArgs = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    rawArgs[_key - 1] = arguments[_key];
  }

  var emCssArgs = css$1.apply(void 0, [strings].concat(rawArgs));
  var transformedStyles = transform(emCssArgs.styles);
  return function (props) {
    return _extends({}, emCssArgs, {
      styles: styleToString(transformedStyles, props)
    });
  };
}

function cx(styles) {
  if (typeof styles === 'string') return styles;
  return function (theme) {
    var p = {
      theme: theme
    };

    function parseStyle(style) {
      if (typeof style === 'object') {
        style = css(style);
      }

      return cascade(style, p);
    }

    if (Array.isArray(styles)) {
      return flatten(styles).map(parseStyle);
    }

    return parseStyle(styles);
  };
}

function jsx(type, props) {
  for (var _len = arguments.length, args = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
    args[_key - 2] = arguments[_key];
  }

  if (props === null || props.css === null) {
    return React.createElement.apply(React, [type, props].concat(args));
  }

  return jsx$1.apply(void 0, [type, _extends({}, props, {
    css: cx(props.css)
  })].concat(args));
}

var createGlobalStyle = function createGlobalStyle() {
  var styles = css.apply(void 0, arguments);

  function GlobalStyle(props) {
    return React.createElement(Global, {
      styles: styles(props)
    });
  }

  return withTheme(GlobalStyle);
};

var Box =
/*#__PURE__*/
emStyled.div(createBox);
/* eslint-disable no-continue, no-loop-func, no-cond-assign */

function flattenArgs(arg, props) {
  if (typeof arg === 'function') return flattenArgs(arg(props), props);
  if (Array.isArray(arg)) return arg.map(function (arg) {
    return flattenArgs(arg, props);
  });
  return arg;
}

function getCreateStyle(baseCreateStyle) {
  return function (strings) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }

    return baseCreateStyle(function (props) {
      var flattenedArgs = flattenArgs(args, props);
      var result = css.apply(void 0, [strings].concat(flattenedArgs))(props);
      return result;
    });
  };
}

function styled(component) {
  return getCreateStyle(emStyled(component));
}

styled.box =
/*#__PURE__*/
styled(Box);
Object.keys(emStyled).forEach(function (key) {
  styled[key] = styled(key);
  styled[key + "Box"] = styled(Box.withComponent(key));
});

function ColorModeStyle(_ref) {
  var targetSelector = _ref.targetSelector;
  var colorModeStyles = React.useCallback(function (theme) {
    return createColorStyles(theme, {
      targetSelector: targetSelector
    });
  }, [targetSelector]);
  return React.createElement(Global, {
    styles: colorModeStyles
  });
}

var ColorModeProvider =
/*#__PURE__*/
createColorModeProvider({
  ThemeContext: ThemeContext,
  ThemeProvider: ThemeProvider,
  ColorModeStyle: ColorModeStyle
});

function useBreakpoints() {
  return useThemeBreakpoints(useTheme());
}

function useBreakpoint() {
  return useThemeBreakpoint(useTheme());
}

function useUp(key) {
  return useThemeUp(useTheme(), key);
}

function useDown(key) {
  return useThemeDown(useTheme(), key);
}

export default styled;
export { Box, ColorModeProvider, createGlobalStyle, css, cx, jsx, useBreakpoint, useBreakpoints, useDown, useUp };